import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import { MessageCircle, ArrowLeft, Radio, MessageSquare, Sparkles, ShieldCheck, Heart, UserPlus, UserCheck, Users, Ban, Undo2 } from "lucide-react";
import { GenderBadge } from "@/components/GenderBadge";

export default function UserProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [recent, setRecent] = useState([]);
  const [browsed, setBrowsed] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    Promise.all([
      api.get(`/users/${encodeURIComponent(username)}`),
      api.get(`/users/${encodeURIComponent(username)}/recent-rooms`).catch(() => ({ data: [] })),
      api.get(`/users/${encodeURIComponent(username)}/browsed-rooms`).catch(() => ({ data: [] })),
    ])
      .then(([p, r, b]) => {
        if (!alive) return;
        setProfile(p.data);
        setRecent(r.data || []);
        setBrowsed(b.data || []);
      })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "User not found"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [username]);

  const joined = profile?.created_at ? new Date(profile.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
  const isOnline = profile?.last_seen && (Date.now() - new Date(profile.last_seen).getTime()) < 2 * 60 * 1000;

  const toggleFollow = async () => {
    if (!profile || profile.is_self || followBusy) return;
    setFollowBusy(true);
    const wasFollowing = profile.am_i_following;
    try {
      if (wasFollowing) {
        const { data } = await api.delete(`/users/${encodeURIComponent(profile.username)}/follow`);
        setProfile((p) => ({ ...p, am_i_following: false, follower_count: data.follower_count }));
        window.dispatchEvent(new CustomEvent("nw:follow-changed"));
        toast(`Unfollowed @${profile.username}`);
      } else {
        const { data } = await api.post(`/users/${encodeURIComponent(profile.username)}/follow`);
        setProfile((p) => ({ ...p, am_i_following: true, follower_count: data.follower_count }));
        window.dispatchEvent(new CustomEvent("nw:follow-changed"));
        toast(`Following @${profile.username}. You'll get a ping when they come online.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update follow.");
    } finally {
      setFollowBusy(false);
    }
  };

  const banUser = async () => {
    if (!profile || profile.is_self) return;
    const ok = window.confirm(
      `Ban @${profile.username}?\n\n` +
      `This permanently deactivates their account and blacklists their last known IP so ` +
      `they can't re-register from the same device. Existing messages stay for audit.\n\n` +
      `This can't be undone.`
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/users/${encodeURIComponent(profile.username)}/ban`);
      setProfile((p) => ({ ...p, banned: true }));
      toast.success(
        `BANNED @${profile.username} · ${data.banned_ips.length} IP${data.banned_ips.length === 1 ? "" : "s"} blacklisted`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to ban user.");
    }
  };

  const unbanUser = async () => {
    if (!profile || !profile.banned) return;
    const ok = window.confirm(
      `Unban @${profile.username}?\n\n` +
      `This reactivates the account and frees any IPs that were blacklisted specifically for them.`
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/users/${encodeURIComponent(profile.username)}/unban`);
      setProfile((p) => ({ ...p, banned: false }));
      toast.success(
        `UNBANNED @${profile.username} · ${data.ips_freed} IP${data.ips_freed === 1 ? "" : "s"} freed`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unban user.");
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-bone" data-testid="user-profile-page">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <button
          onClick={() => nav(-1)}
          data-testid="btn-back"
          className="font-mono text-xs text-white/50 hover:text-acid uppercase tracking-widest flex items-center gap-1 mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </button>

        {loading && <div className="font-mono text-acid">LOADING…</div>}
        {err && <div className="border-2 border-blaze text-blaze font-mono text-xs p-3">{err}</div>}

        {profile && (
          <>
            <div className="border-2 border-acid bg-panel/60 p-6 md:p-8">
              <div className="flex items-start gap-5 flex-wrap">
                <div className="relative">
                  <img
                    src={profile.avatar}
                    alt=""
                    className="w-20 h-20 md:w-24 md:h-24 object-cover border-2 border-white/10"
                    data-testid="profile-avatar"
                  />
                  {isOnline && (
                    <span
                      className="absolute -bottom-1 -right-1 w-4 h-4 bg-acid border-2 border-obsidian animate-pulse"
                      data-testid="online-dot"
                      title="Online now"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1
                      className="font-heading text-3xl sm:text-4xl md:text-5xl leading-none break-all"
                      data-testid="profile-username"
                      style={{ color: profile.text_color || "#F4F4F0", overflowWrap: "anywhere" }}
                    >
                      @{profile.username}
                    </h1>
                    <GenderBadge gender={profile.gender} className="text-3xl md:text-4xl leading-none" />
                    {profile.is_pro && (
                      <span className="font-mono text-[10px] text-obsidian bg-acid px-2 py-0.5 uppercase tracking-widest flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Pro
                      </span>
                    )}
                    {profile.is_guest && (
                      <span className="font-mono text-[10px] text-white/60 border border-white/20 px-2 py-0.5 uppercase tracking-widest">
                        Guest
                      </span>
                    )}
                    {isOnline && (
                      <span className="font-mono text-[10px] text-acid uppercase tracking-widest">// online</span>
                    )}
                  </div>
                  <div className="font-mono text-[11px] text-white/40 mt-2 flex items-center gap-2 flex-wrap">
                    <ShieldCheck className="w-3 h-3" /> Joined {joined || "—"}
                  </div>
                  {profile.bio && (
                    <p className="font-body text-white/80 mt-4 whitespace-pre-wrap break-words" data-testid="profile-bio">
                      {profile.bio}
                    </p>
                  )}
                  {!profile.bio && (
                    <p className="font-mono text-xs text-white/30 mt-4">// no bio yet</p>
                  )}

                  <div className="mt-4 flex gap-6 font-mono text-xs text-white/60">
                    <div data-testid="follower-count">
                      <span className="text-hotpink font-bold text-lg mr-1">{profile.follower_count ?? 0}</span> Followers
                    </div>
                    <div data-testid="following-count">
                      <span className="text-acid font-bold text-lg mr-1">{profile.following_count ?? 0}</span> Following
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3 flex-wrap">
                {profile.is_self ? (
                  <Link to="/profile" data-testid="btn-edit-profile" className="btn-acid flex items-center gap-2">
                    Edit profile
                  </Link>
                ) : (
                  <>
                    {!profile.is_guest && (
                      <button
                        type="button"
                        onClick={toggleFollow}
                        disabled={followBusy || user?.is_guest}
                        data-testid={profile.am_i_following ? "btn-unfollow" : "btn-follow"}
                        className={
                          profile.am_i_following
                            ? "font-mono text-xs px-4 py-2 border-2 border-hotpink text-hotpink hover:bg-hotpink hover:text-obsidian uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
                            : "btn-acid flex items-center gap-2 disabled:opacity-50"
                        }
                        title={user?.is_guest ? "Sign up to follow people" : ""}
                      >
                        {profile.am_i_following ? (
                          <>
                            <UserCheck className="w-4 h-4" /> Following
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4" /> Follow
                          </>
                        )}
                      </button>
                    )}
                    {!profile.is_guest && (
                      <Link
                        to={`/dm/${profile.id}`}
                        data-testid="btn-send-dm"
                        className="font-mono text-xs px-4 py-2 border-2 border-acid text-acid hover:bg-acid hover:text-obsidian uppercase tracking-widest flex items-center gap-2"
                      >
                        <MessageCircle className="w-4 h-4" /> Send DM
                      </Link>
                    )}
                    {user?.is_admin && !profile.banned && (
                      <button
                        type="button"
                        onClick={banUser}
                        data-testid="btn-ban-user"
                        className="font-mono text-xs px-4 py-2 border-2 border-blaze text-blaze hover:bg-blaze hover:text-obsidian uppercase tracking-widest flex items-center gap-2"
                        title={`Ban @${profile.username}`}
                      >
                        <Ban className="w-4 h-4" /> Ban user
                      </button>
                    )}
                    {user?.is_admin && profile.banned && (
                      <button
                        type="button"
                        onClick={unbanUser}
                        data-testid="btn-unban-user"
                        className="font-mono text-xs px-4 py-2 border-2 border-acid text-acid hover:bg-acid hover:text-obsidian uppercase tracking-widest flex items-center gap-2"
                        title={`Unban @${profile.username}`}
                      >
                        <Undo2 className="w-4 h-4" /> Unban user
                      </button>
                    )}
                    {profile.banned && !user?.is_admin && (
                      <span
                        data-testid="banned-badge"
                        className="font-mono text-xs px-4 py-2 border-2 border-blaze text-blaze uppercase tracking-widest flex items-center gap-2"
                      >
                        <Ban className="w-4 h-4" /> Banned
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="border-2 border-hotpink bg-panel/60 p-4" data-testid="stat-messages">
                <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> Messages
                </div>
                <div className="font-heading text-4xl text-hotpink mt-1">{profile.messages_sent ?? 0}</div>
              </div>
              <div className="border-2 border-acid bg-panel/60 p-4" data-testid="stat-rooms">
                <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest flex items-center gap-1">
                  <Radio className="w-3 h-3" /> Rooms created
                </div>
                <div className="font-heading text-4xl text-acid mt-1">{profile.rooms_created ?? 0}</div>
              </div>
            </div>

            <section className="mt-6" data-testid="recent-rooms">
              <div className="flex items-center gap-2 mb-3 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                <MessageSquare className="w-3 h-3" /> Active in
              </div>
              {recent.length === 0 ? (
                <div className="border-2 border-white/10 bg-panel/40 p-4 font-mono text-xs text-white/40">
                  {profile.is_self ? "// you haven't posted in any rooms yet" : `// @${profile.username} hasn't posted anywhere yet`}
                </div>
              ) : (
                <div className="space-y-2">
                  {recent.map((r) => (
                    <Link
                      key={r.slug}
                      to={`/room/${r.slug}`}
                      data-testid={`recent-room-${r.slug}`}
                      className="block border-2 border-white/10 bg-panel/40 hover:border-acid p-4 group transition-colors"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-heading text-2xl text-acid group-hover:text-hotpink truncate">
                            {r.name}
                          </div>
                          <div className="font-mono text-[10px] text-white/40 mt-1 truncate">// {r.topic}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono text-xs text-white/60">{r.messages} msg{r.messages === 1 ? "" : "s"}</div>
                          <div className="font-mono text-[10px] text-white/30 mt-1">
                            {new Date(r.last_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            <section className="mt-6" data-testid="browsed-rooms">
              <div className="flex items-center gap-2 mb-3 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                <Radio className="w-3 h-3" /> Browsed
              </div>
              {browsed.length === 0 ? (
                <div className="border-2 border-white/10 bg-panel/40 p-4 font-mono text-xs text-white/40">
                  {profile.is_self ? "// walk into a room to fill this out" : `// @${profile.username} hasn't wandered into any rooms yet`}
                </div>
              ) : (
                <div className="space-y-2">
                  {browsed.map((r) => (
                    <Link
                      key={r.slug}
                      to={`/room/${r.slug}`}
                      data-testid={`browsed-room-${r.slug}`}
                      className="block border-2 border-white/10 bg-panel/40 hover:border-cyan_wire p-4 group transition-colors"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-heading text-2xl text-cyan_wire group-hover:text-hotpink truncate">
                            {r.name}
                          </div>
                          <div className="font-mono text-[10px] text-white/40 mt-1 truncate">// {r.topic}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono text-xs text-white/60">{r.visits} visit{r.visits === 1 ? "" : "s"}</div>
                          <div className="font-mono text-[10px] text-white/30 mt-1">
                            {new Date(r.last_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

